import React from 'react';
import { twMerge } from 'tailwind-merge';
import { PostInputUI } from '@components/Feed/PostInputUI';
import { CollectModal } from '@components/modals/Collect/CollectModal';
import { CommentModalV2 } from '@components/modals/CommentV2';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import type { SongViewFragment } from '@graphql/reactQuery';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import { useStableCallback } from '@hooks/useStableCallback';

export function CommentBar({
  releaseFrag,
  sticky,
}: {
  releaseFrag: SongViewFragment;
  sticky: {
    isSticky: boolean;
    offset: number | undefined;
  } | null;
}) {
  const releaseId = releaseFrag.id;

  const { openModal } = ModalContext.useContainer();
  const { login } = useConnectedAuth();

  const { isAuthenticatedWithAPI } = useAuth();
  const { isReleaseOwner } = useNftOwnershipData({
    releaseId: releaseFrag.id,
  });

  const placeholder = isReleaseOwner ? 'Write a comment...' : 'Collect to comment...';

  const openCommentModal = () => {
    openModal(ModalType.COMMENT_MODAL_V2, {
      body: <CommentModalV2 releaseId={releaseId} />,
    });
  };

  const openCollectModal = () => {
    openModal(ModalType.COLLECT, {
      body: (
        <CollectModal mintReferral={null} release={releaseFrag} collectButtonSource="Comment Bar" />
      ),
    });
  };

  const showModal = () => {
    if (isReleaseOwner) {
      return openCommentModal();
    } else {
      return openCollectModal();
    }
  };

  const onCommentButtonClick = useStableCallback(async () => {
    if (!isAuthenticatedWithAPI) {
      const loginResult = await login();

      if (loginResult.status === 'success') {
        showModal();
      } else {
        return;
      }
    } else {
      showModal();
    }
  });

  return (
    <div
      className={twMerge('py-4', sticky?.isSticky && 'top sticky z-stickyHeader -mx-1 bg-white')}
      style={sticky?.offset != null ? { top: sticky.offset } : undefined}
    >
      <PostInputUI
        onClick={onCommentButtonClick}
        placeholder={placeholder}
        isLocked={false}
        as="default"
        type="feed_post"
      />
    </div>
  );
}
